import React, { useState, useEffect } from "react";
import { compareObjects } from "./utils";
import { usePopup } from "./PopupContext";
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4242/api/v1";

const ArchetypeForm = (props) => {
    const [active, setActive] = useState(props.selectedArchetype.active)
    const [showChoice, setShowChoise] = useState(props.selectedArchetype.showChoice)
    const [title, setTitle] = useState(props.selectedArchetype.title)
    const [heyphenTitle, setHyphenTitle] = useState(props.selectedArchetype.hyphenated_title)
    const [description, setDescription] = useState(props.selectedArchetype.description)
    const [searchTerms, setSearchTerms] = useState(props.selectedArchetype.searchTerms)
    const [pronoun1, setPronoun1] = useState(props.selectedArchetype.pronoun1)
    const [pronoun2, setPronoun2] = useState(props.selectedArchetype.pronoun2)
    const [presumedGender, setPresumedGender] = useState(props.selectedArchetype.presumedGender)
    const [profilePictureFile, setProfilePictureFile] = useState(null);
    const [profilePictureURL, setProfilePictureURL] = useState(props.selectedArchetype.profilePictureURL)
    const pop = usePopup()

    useEffect(() => {
        return () => {
            if (profilePictureURL?.startsWith("blob:")) {
                URL.revokeObjectURL(profilePictureURL);
            }
        };
    }, [profilePictureURL]);

    const handleSetSearchTerm = (term, index) => {
        setSearchTerms(prevTerms => {
            const newTerms = [...prevTerms]
            newTerms[index] = term
            return newTerms.filter(t => t.length > 0) // removes unused terms
        })
    }

    const handleAddTerm = (e) => {
        e.preventDefault()
        setSearchTerms(prevTerms => {
            const newTerms = [...prevTerms]
            newTerms.push("")
            return newTerms
        })
    }

    const handleFormSubmit = async (e) => {
        e.preventDefault()
        let updatedArchetype = {...props.selectedArchetype}

        // handle new profile picture upload
        if (profilePictureFile) {
            try {
                // 1. Request presigned upload URL from backend
                const response = await fetch(
                    `${API_URL}/admin/generate-guide-type-picture-url`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${window.localStorage.getItem('adminToken')}`
                        },
                        body: JSON.stringify({
                            fileName: profilePictureFile.name,
                            fileType: profilePictureFile.type,
                            guideType: updatedArchetype._id.toString()
                        }),
                    }
                );
                
                const data = await response.json();

                if (!data.success) {
                    throw new Error("Failed to generate upload URL");
                }

                const { uploadUrl, viewUrl, imageKey } = data;

                // 2. Upload file directly to S3
                const uploadResponse = await fetch(uploadUrl, {
                    method: "PUT",
                    body: profilePictureFile,
                    headers: {
                        "Content-Type": profilePictureFile.type
                    }
                });

                if (!uploadResponse.ok) {
                    const errorText = await uploadResponse.text();
                    console.error("S3 upload error:", errorText);
                }
                    
                setProfilePictureURL(viewUrl);
                buildAndReturnGuideType(updatedArchetype, imageKey)

            } catch (error) {
                console.error("Profile picture upload failed:", error);
                pop(error.message, 'flash', false);
            }
        } else {
            buildAndReturnGuideType(updatedArchetype)
        }

        setSearchTerms(updatedArchetype.searchTerms) // removes unused terms
    }  

    const buildAndReturnGuideType = (updatedArchetype, profilePictureKey = false) => {
        // build updated document
        updatedArchetype.active = active
        updatedArchetype.showChoice = showChoice
        updatedArchetype.title = title
        updatedArchetype.hyphenated_title = heyphenTitle
        updatedArchetype.description = description
        updatedArchetype.searchTerms = searchTerms.filter(t => t.length > 0)
        updatedArchetype.pronoun1 = pronoun1
        updatedArchetype.pronoun2 = pronoun2
        updatedArchetype.presumedGender = presumedGender
        // this overwrites profilePictureKey with new one
        if (profilePictureKey) {
            updatedArchetype.profilePictureKey = profilePictureKey
            updatedArchetype.prevProfilePictureKey = props.selectedArchetype.profilePictureKey
        }

        if ( compareObjects(updatedArchetype, props.selectedArchetype) ) {
            // No change, do nothing
            console.log("no changes, accidental push?")
        } else {
            delete updatedArchetype.profilePictureURL
           return props.updateArchetype(updatedArchetype)
        }
    }

    const handleFileUpdate = (file) => {
        if (!file) return;
        setProfilePictureFile(file)
        const previewUrl = URL.createObjectURL(file);
        setProfilePictureURL(previewUrl);
    };

    const handleBackButton = (e) => {
        e.preventDefault()
        props.setShowArchetypeDetails(false)
    }
    
    const handleRejectArchetype = (e) => {
        e.preventDefault()
        props.rejectArchetype(props.selectedArchetype)
    }
    return (
            <form className="form">
                <h3>Archetype Details</h3>
                <div className="form-group">
                    <label>Archetype Image</label>
                    {profilePictureURL && <img src={profilePictureURL} alt="Profile" style={{ width: 150, height: "auto" }} />}
                    <input type="file" accept="image/*" onChange={(e) => handleFileUpdate(e.target.files[0])} />
                </div>
                <div className="form-group form-group-row">
                    <div className="form-group inline">
                        <label>Active</label>
                        <label className="switch">
                            <input type="checkbox" checked={active} onChange={() => setActive(!active)} />
                            <span className="slider"></span>
                        </label>

                        <label style={{marginLeft:"1rem"}}>Show Choice</label>
                        <label className="switch">
                            <input type="checkbox" checked={showChoice} onChange={() => setShowChoise(!showChoice)} />
                            <span className="slider"></span>
                        </label>
                    </div>
                </div>
                <div className="form-group form-group-row">
                    <div className="form-group">
                        <label>Title</label>
                        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label>Hyphenated Title</label>
                        <input type="text" value={heyphenTitle} onChange={(e) => setHyphenTitle(e.target.value)} />
                    </div>
                </div>
                <div className="form-group">
                    <label>Description</label>
                    <textarea type="textarea" value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>
                <div className="form-group">
                    <label>Search Terms</label>
                    {searchTerms.map((term,i) => <input type="text" key={`term-${i}`} value={term} onChange={(e) => handleSetSearchTerm(e.target.value, i)} />)}
                    <button className="manage-item-action add-input" onClick={handleAddTerm}>+</button>
                </div>
                
                <div className="form-group">
                    <label>Presumed Gender</label>
                    <select value={presumedGender} onChange={(e) => setPresumedGender(e.target.value)}>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="either">Either</option>
                    </select>
                </div>

                <div className="form-actions">
                    <button className="manage-item-action default" onClick={handleBackButton}>Back to List</button>
                    <button className="manage-item-action red" onClick={handleRejectArchetype}>Reject</button>
                    <button className="manage-item-action boggler" onClick={handleFormSubmit}>Update</button>
                </div>
            </form>
    );
}

export default ArchetypeForm;